import makeStyles from '@mui/styles/makeStyles';
import type { Theme } from '@mui/material/styles';

// Figma "Input with Title": 14/19 label with a 12px gap to the input
// (the p81 TextField default is a 17px line box with no gap). Only inputs
// nested in a field label are targeted - checkbox labels have no
// FormControl child.
// NOTE: both selectors reach into @p81-common/ui internals (the label
// element wraps the FormControl; LabelWithHint is a generated class name).
// Re-verify the association screens against Figma after a ui-lib bump.
export const labeledInputSpacing = {
  '& label > .MuiFormControl-root': {
    marginTop: '12px'
  },
  '& label [class*="LabelWithHint-root"]': {
    lineHeight: '19px'
  }
};

export const stepContainer = (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: theme.spacing(2),
  ...labeledInputSpacing
});

export const stepSection = (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: theme.spacing(0.5)
});

// Figma info box (73:1782 "How to get the API key" pattern): Secondary/100
// light-blue background, 8px radius, 16px padding, 12px gap, H6 title
// (14/19 medium).
export const createNeutralInfoBoxClasses = makeStyles(theme => ({
  box: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: '8px',
    backgroundColor: theme.palette.secondary[100]
  },
  title: {
    fontWeight: 500,
    lineHeight: '19px',
    color: theme.palette.text.primary
  }
}));

// Light-blue helper strip shown right under an input, per the Figma
// "Card Attention Box" (520x32: Secondary/100 background, caption text).
export const hintStrip = (theme: Theme) => ({
  display: 'block',
  padding: theme.spacing(1, 1.5),
  borderRadius: '4px',
  backgroundColor: theme.palette.secondary[100],
  color: theme.palette.primary[400],
  fontSize: '12px',
  lineHeight: '15px'
});

// A validation failure and its rule id. The message carries the detail, so the
// rule sits under it as a caption rather than competing with it.
export const createRuleViolationListClasses = makeStyles(theme => ({
  list: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1.5)
  },
  item: {
    display: 'flex',
    flexDirection: 'column' as const,
    // 2px glued the rule id to the message it belongs to; 4px keeps them paired
    // while letting the message read as its own line.
    gap: theme.spacing(0.5)
  },
  message: {
    lineHeight: '19px',
    wordBreak: 'break-word' as const
  },
  rule: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '15px'
  }
}));

// Figma "confirm checkbox" pattern (272-series): 16px checkbox nudged 4px
// down, 12px gap, 14/17 label in Primary/500, 8px bottom padding.
export const checkboxLabel = (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'row' as const,
  alignItems: 'flex-start' as const,
  gap: '12px',
  paddingBottom: theme.spacing(1),
  whiteSpace: 'break-spaces' as const,
  cursor: 'pointer' as const,
  '& span': {
    padding: '4px 0 0 0'
  },
  '& p': {
    fontSize: '14px',
    lineHeight: '17px',
    color: theme.palette.primary[500]
  }
});

export const createClasses = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    gap: theme.spacing(1.5),
    // The library forces minHeight 88px; Figma's md form box is 72px
    // (16px padding + 40px icon + 16px padding).
    minHeight: 0
  },
  // Figma "Form Attention Box" md: 40px illustration, H4 title (16/21 medium)
  // vertically centered against the icon.
  title: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: 500,
    lineHeight: '21px'
  },
  icon: {
    width: '40px',
    height: '40px'
  },
  contentInner: {
    alignItems: 'center'
  },
  // AttentionBox requires a subtitle string; when we pass '' it still renders
  // an empty line that pushes the title off-center - collapse it.
  subtitle: {
    '&:empty': {
      display: 'none'
    }
  },
  // Same for the buttons container: when no buttons are passed it still
  // renders 0px-high and eats a 12px flex gap above it.
  buttons: {
    '&:empty': {
      display: 'none'
    }
  },
  // AttentionBox's default titleContainer is `flexDirection: row`, which lines
  // up title/subtitle, children, and buttons horizontally - that squeezes the
  // title into a narrow strip and pushes children into a side column. Force
  // column direction so children stack below the title at full width while
  // the icon stays in the outer grid's left column next to the title.
  titleContainer: {
    flexDirection: 'column' as const,
    gap: theme.spacing(1.5)
  },
  // The library sets textContainer to `alignSelf: 'center'` for the
  // non-vertical layout. Combined with our column-direction titleContainer
  // override, that centers the title+subtitle block horizontally when there
  // is no subtitle to stretch it. Pin to the start so the title stays next
  // to the icon regardless of whether the consumer passes a subtitle.
  textContainer: {
    alignSelf: 'flex-start' as const
  }
}));
